<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import CCard from '@lib/components/CCard/CCard.vue'
import CButton from '@lib/components/CButton/CButton.vue'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import ContentSelector from '@content-selector/App.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { RecycleScroller } from 'vue-virtual-scroller'
import type { File, FileRetrieve, Folder, FolderRetrieve } from '@openapi'
import type { SelectedFile } from '@content-selector/stores/app'
import { isModifierClick } from '@lib/util/navigation.util'

type Item = { file: { id: File['id']; name: File['name'] }; id: string }

interface Props {
  items: Item[]
  onFileOpen: (id: File['id'], newTab?: boolean) => void
  onFileFetch: (id: File['id']) => Promise<FileRetrieve>
  onFolderFetch: (id?: Folder['id']) => Promise<FolderRetrieve>
  onFilesList: (params?: any) => Promise<any[]>
  selectedFilesIds?: File['id'][]
  maxItemsToRender?: number
  title?: string
  primaryActionText?: string
}

const props = withDefaults(defineProps<Props>(), {
  items: Array,
  maxItemsToRender: 5,
  maxSelectItemsToRender: 5,
})

const emit = defineEmits<{
  (e: 'update', item: Item): void
  (e: 'delete', item: Item): void
  (e: 'view', item: Item, newTab?: boolean): void
  (e: 'select', selectedFiles: SelectedFile[]): void
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const SCROLLER_ITEM_SIZE = 44

const showContentSelector = ref(false)

function deleteItem(item: Item) {
  emit('delete', item)
}

const displayTitle = computed(
  () => props.title ?? t('canvasUI.components.CFilesAccessManage.files', props.items.length),
)

const selections = computed(() =>
  props.selectedFilesIds?.map((id) => {
    return { fileId: id, type: 'file' as const }
  }),
)

const onPrimaryAction = (payload: { selectedItems: SelectedFile[] }) => {
  emit('select', payload.selectedItems)
  showContentSelector.value = false
}

function onView(item: Item, e: MouseEvent) {
  emit('view', item, isModifierClick(e))
}
</script>

<template>
  <CCard bordered size="medium">
    <div>
      <div class="flex items-center justify-between gap-1">
        <div class="flex gap-4 items-center">
          <CIcon icon="folder" size="14" />

          <div class="flex font-bold">
            {{ displayTitle }}
          </div>
        </div>

        <CButton iconPlacement="left" secondary @click="showContentSelector = true">
          {{ t('canvasUI.components.CFilesAccessManage.select') }}
          <template #icon>
            <CIcon icon="file-check" size="16" />
          </template>
        </CButton>

        <ContentSelector
          v-if="showContentSelector"
          app-name="admin"
          content-type="file"
          :minSelections="0"
          :on-file-fetch="(id) => onFileFetch(id)"
          :on-file-open="({ fileId, newTab }) => onFileOpen(fileId, newTab)"
          :on-files-list="(params) => onFilesList(params)"
          :on-folder-fetch="(id) => onFolderFetch(id)"
          :primaryActionText="primaryActionText ?? t('canvasUI.components.CFilesAccessManage.primaryActiontext')"
          primaryAction="select"
          :selections="selections"
          @cancel="showContentSelector = false"
          @primaryAction="onPrimaryAction"
        />
      </div>
    </div>
    <RecycleScroller
      v-slot="{ item: item }: { item: Item }"
      :buffer="SCROLLER_ITEM_SIZE * maxItemsToRender * 2"
      class="mt-4 recycle-scroller"
      :itemSize="SCROLLER_ITEM_SIZE"
      :items="items"
      keyField="id"
      :style="items.length > maxItemsToRender ? { maxHeight: `${SCROLLER_ITEM_SIZE * maxItemsToRender}px` } : undefined"
    >
      <div class="flex items-center justify-between gap-1">
        <div class="flex items-center gap-1">
          <div class="font-bold">{{ item.file.name }}</div>
        </div>

        <div class="flex items-center gap-4 mr-2">
          <CButton circle :color="themeVars.error2" @click="deleteItem(item)">
            <template #icon>
              <CIcon class="ml-1" :color="themeVars.error" icon="file-minus" size="medium" />
            </template>
          </CButton>
          <CButton circle @click="onView(item, $event)">
            <template #icon>
              <CIcon icon="eye" size="medium" />
            </template>
          </CButton>
        </div>
      </div>
    </RecycleScroller>
  </CCard>
</template>
